import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Package, Thermometer, Scale, TrendingUp, Calendar } from 'lucide-react';
import type { Order } from '../types/Order';
import { getTimeUntilNextInspection, getStatusColor, getStatusLabel } from '../types/Order';
interface OrderCardProps {
  order: Order;
  onClick: () => void;
}
export function OrderCard({
  order,
  onClick
}: OrderCardProps) {
  const [timeUntilNext, setTimeUntilNext] = useState('');
  const statusColor = getStatusColor(order.status);
  const statusLabel = getStatusLabel(order.status);
  useEffect(() => {
    if (order.status === 'active') {
      const updateTime = () => {
        setTimeUntilNext(getTimeUntilNextInspection(order.nextInspection));
      };
      updateTime();
      const interval = setInterval(updateTime, 1000);
      return () => clearInterval(interval);
    }
  }, [order.nextInspection, order.status]);
  const colorClasses = {
    blue: {
      badge: 'bg-blue-100 text-blue-700',
      border: 'border-blue-200',
      headerBg: 'bg-blue-50'
    },
    yellow: {
      badge: 'bg-yellow-100 text-yellow-700',
      border: 'border-yellow-200',
      headerBg: 'bg-yellow-50'
    },
    green: {
      badge: 'bg-green-100 text-green-700',
      border: 'border-green-200',
      headerBg: 'bg-green-50'
    }
  };
  const colors = colorClasses[statusColor];
  const formatDuration = (startedAt: Date, completedAt?: Date) => {
    const end = completedAt || new Date();
    const diff = end.getTime() - startedAt.getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor(diff % 3600000 / 60000);
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  };
  return <motion.button initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} onClick={onClick} className={`w-full text-left bg-white rounded-xl border-2 ${colors.border} overflow-hidden hover:shadow-lg transition-shadow cursor-pointer`} data-id="element-410">
      {/* Header */}
      <div className={`${colors.headerBg} px-6 py-4 border-b-2 ${colors.border}`} data-id="element-411">
        <div className="flex items-start justify-between" data-id="element-412">
          <div className="space-y-1" data-id="element-413">
            <h3 className="text-xl font-semibold text-gray-900" data-id="element-414">
              {order.produktName}
            </h3>
            <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-415">
              <span data-id="element-416">Charge: {order.chargenNummer}</span>
              <span data-id="element-417">•</span>
              <span data-id="element-418">Nr: {order.produktNr}</span>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors.badge}`} data-id="element-419">
            {statusLabel}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6" data-id="element-420">
        {/* Prüflinge & Linie */}
        <div className="grid grid-cols-2 gap-4" data-id="element-421">
          <div className="space-y-1" data-id="element-422">
            <div className="flex items-center gap-2 text-gray-500" data-id="element-423">
              <Package className="w-4 h-4" strokeWidth={2} data-id="element-424" />
              <span className="text-xs font-medium uppercase tracking-wide" data-id="element-425">
                Geprüfte Prüflinge
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-426">
              {order.prueflingeGeprueft}
            </p>
          </div>

          <div className="space-y-1" data-id="element-427">
            <div className="flex items-center gap-2 text-gray-500" data-id="element-428">
              <TrendingUp className="w-4 h-4" strokeWidth={2} data-id="element-429" />
              <span className="text-xs font-medium uppercase tracking-wide" data-id="element-430">
                Linie
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900" data-id="element-431">{order.linie}</p>
          </div>
        </div>

        {/* Durchschnittswerte */}
        <div className="space-y-3" data-id="element-432">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide" data-id="element-433">
            Durchschnittswerte
          </h4>
          <div className="grid grid-cols-2 gap-4" data-id="element-434">
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-100" data-id="element-435">
              <div className="flex items-center gap-2 mb-2" data-id="element-436">
                <Thermometer className="w-5 h-5 text-orange-600" strokeWidth={2} data-id="element-437" />
                <span className="text-xs font-medium text-orange-700 uppercase tracking-wide" data-id="element-438">
                  Temperatur
                </span>
              </div>
              <p className="text-2xl font-bold text-orange-900" data-id="element-439">
                {order.avgTemperature.toFixed(1)} °C
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-4 border border-green-100" data-id="element-440">
              <div className="flex items-center gap-2 mb-2" data-id="element-441">
                <Scale className="w-5 h-5 text-green-600" strokeWidth={2} data-id="element-442" />
                <span className="text-xs font-medium text-green-700 uppercase tracking-wide" data-id="element-443">
                  Gewicht
                </span>
              </div>
              <p className="text-2xl font-bold text-green-900" data-id="element-444">
                {order.avgWeight.toFixed(2)} kg
              </p>
            </div>
          </div>
        </div>

        {/* Zeitinformationen */}
        <div className="space-y-3 pt-3 border-t border-gray-100" data-id="element-445">
          <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-446">
            <div data-id="element-447">
              <p className="text-gray-500 mb-1" data-id="element-448">Gestartet</p>
              <p className="font-medium text-gray-900" data-id="element-449">
                {order.startedAt.toLocaleString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
              </p>
            </div>

            {order.status === 'active' && <div data-id="element-450">
                <p className="text-gray-500 mb-1" data-id="element-451">Nächste Prüfung</p>
                <p className="font-semibold text-blue-600" data-id="element-452">{timeUntilNext}</p>
              </div>}

            {order.status === 'paused' && <div data-id="element-453">
                <p className="text-gray-500 mb-1" data-id="element-454">Letzte Prüfung</p>
                <p className="font-medium text-gray-900" data-id="element-455">
                  {order.lastInspection.toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit'
              })}
                </p>
              </div>}

            {order.status === 'completed' && order.completedAt && <div data-id="element-456">
                <p className="text-gray-500 mb-1" data-id="element-457">Abgeschlossen</p>
                <p className="font-medium text-gray-900" data-id="element-458">
                  {order.completedAt.toLocaleString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
                </p>
              </div>}
          </div>

          {(order.status === 'completed' || order.status === 'paused') && <div className="pt-2" data-id="element-459">
              <p className="text-gray-500 text-sm mb-1" data-id="element-460">Gesamtdauer</p>
              <p className="font-semibold text-gray-900" data-id="element-461">
                {formatDuration(order.startedAt, order.completedAt)}
              </p>
            </div>}
        </div>
      </div>
    </motion.button>;
}